import React, { useState, useEffect } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import InternationalPhoneInput from '../../common/InternationalPhoneInput';
import { apiService } from '../../../services/api';
import './Registration.scss';

interface EventField {
  id: number;
  name: string;
  label: string;
  field_type: string;
  required: boolean;
  options?: { [key: string]: string };
}

interface CategoryField {
  id: number;
  name: string;
  label: string;
  field_type: string;
  required: boolean;
  options?: { [key: string]: string };
}

interface GeneralRegistrationProps {
  event: any;
  category: any;
  user?: any;
  onSuccess?: (data: any) => void;
  onCancel?: () => void;
}

interface AttendeeData {
  name: string;
  email: string;
  phone: string;
  [key: string]: any;
}

const GeneralRegistration: React.FC<GeneralRegistrationProps> = ({ 
  event, 
  category, 
  user,
  onSuccess, 
  onCancel 
}) => {
  const [formData, setFormData] = useState<{[key: string]: any}>({
    name: '',
    email: '',
    phone: '',
    quantity: 1
  });
  
  const [additionalAttendees, setAdditionalAttendees] = useState<AttendeeData[]>([]);
  const [eventFields, setEventFields] = useState<EventField[]>([]);
  const [categoryFields, setCategoryFields] = useState<CategoryField[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Load event and category fields on component mount
  useEffect(() => {
    if (event?.fields) {
      setEventFields(event.fields);
    }
    if (category?.fields) {
      setCategoryFields(category.fields);
    }
  }, [event, category]);

  // Prefill user information if available
  useEffect(() => {
    const getUserData = () => {
      // Try multiple sources for user data - prioritize passed user prop
      const userData = user || event?.user || event?.current_user || event?.profile || {};
      
      return {
        name: userData.name || userData.full_name || (userData.first_name && userData.last_name ? `${userData.first_name} ${userData.last_name}` : '') || '',
        email: userData.email || userData.email_address || '',
        phone: userData.phone || userData.phone_number || userData.mobile || userData.contact_number || userData.profile?.phone_number || '',
      };
    };

    const prefilledData = getUserData();

    // Also check for previously submitted form data
    const previousData = user?.previous_registration || event?.user?.previous_registration || event?.user?.form_data || {};

    // Combine user data with previous form data
    const allPrefilledData = {
      ...prefilledData,
      ...previousData
    };

    // Only update if we have some user data and current form is empty
    if ((allPrefilledData.name || allPrefilledData.email || allPrefilledData.phone || Object.keys(previousData).length > 0) && 
        (!formData.name && !formData.email && !formData.phone)) {
      setFormData(prev => ({
        ...prev,
        ...allPrefilledData
      }));
    }
  }, [user, event?.user, event?.current_user, event?.profile]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (value: string | undefined) => {
    setFormData(prev => ({ ...prev, phone: value || '' }));
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const quantity = parseInt(e.target.value);
    setFormData(prev => ({ ...prev, quantity }));
    
    // Update additional attendees based on quantity
    const additionalCount = quantity > 1 ? quantity - 1 : 0;
    if (additionalCount !== additionalAttendees.length) {
      const newAttendees: AttendeeData[] = [];
      for (let i = 0; i < additionalCount; i++) {
        newAttendees.push({
          name: '',
          email: '',
          phone: '',
          // Initialize dynamic fields
          ...Object.fromEntries(
            [...eventFields, ...categoryFields].map(field => [field.name, ''])
          )
        });
      }
      setAdditionalAttendees(newAttendees);
    }
  };

  const handleAttendeeChange = (index: number, field: string, value: string) => {
    setAdditionalAttendees(prev => 
      prev.map((attendee, i) => 
        i === index ? { ...attendee, [field]: value } : attendee
      )
    );
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields before submission
      if (!formData.name || !formData.name.trim()) {
        setError('Full name is required');
        setLoading(false);
        return;
      }
      if (!formData.email || !formData.email.trim()) {
        setError('Email address is required');
        setLoading(false);
        return;
      }
      if (!formData.phone || !formData.phone.trim()) {
        setError('Phone number is required');
        setLoading(false);
        return;
      }
      if (!formData.quantity || formData.quantity < 1) {
        setError('Please select a quantity');
        setLoading(false);
        return;
      }

      // Prepare registration data
      const registrationData = {
        event_slug: event.slug,
        category_id: category.id,
        name: formData.name.trim(), // API expects 'name', not 'first_name' and 'last_name'
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        quantity: parseInt(formData.quantity),
        // Include dynamic fields as custom_fields
        custom_fields: Object.fromEntries(
          Object.entries(formData).filter(([key]) => 
            !['name', 'email', 'phone', 'quantity'].includes(key)
          ).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])
        )
      };

      console.log('General registration data:', registrationData);

      const response = await apiService.registration.general(registrationData);

      if (response.success) {
        setSuccess(true);
        
        // Dispatch cart update event to notify header
        window.dispatchEvent(new CustomEvent('cartUpdated', {
          detail: { message: 'Cart updated successfully' }
        }));
        
        onSuccess?.(response);
      } else {
        // Handle different error response formats
        let errorMessage = 'Registration failed';
        if (response.error) {
          errorMessage = response.error;
        } else if (response.errors) {
          // Handle field-specific errors
          const errorMessages = Object.entries(response.errors).map(([field, message]) => {
            return `${field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}: ${message}`;
          });
          errorMessage = errorMessages.join('. ');
        } else if (response.message) {
          errorMessage = response.message;
        }
        setError(errorMessage);
      }
    } catch (err: any) {
      console.error('General registration error:', err);
      
      // Extract error message from different response formats
      let errorMessage = 'Registration failed. Please try again.';
      
      if (err.response?.data) {
        const errorData = err.response.data;
        if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.errors) {
          // Handle field-specific errors
          if (typeof errorData.errors === 'object') {
            const errorMessages = Object.entries(errorData.errors).map(([field, message]) => {
              return `${field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}: ${message}`;
            });
            errorMessage = errorMessages.join('. ');
          } else {
            errorMessage = String(errorData.errors);
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="general-registration">
        <div className="glass p-4 rounded-3 q-shadow text-center">
          <div className="success-icon mb-3">
            <i className="fas fa-check-circle text-success" style={{ fontSize: '3rem' }}></i>
          </div>
          <h3 className="text-success mb-3">Registration Added to Cart!</h3>
          <p className="text-muted mb-4">
            <strong>{category.name}</strong> has been successfully added to your cart.
          </p>
          <p className="text-muted mb-4">
            You can now proceed to checkout to complete your purchase and finalize your registration.
          </p>
          <div className="d-flex gap-3 justify-content-center">
            <button 
              type="button"
              className="btn btn-outline-secondary"
              onClick={onCancel}
            >
              Continue Shopping
            </button>
            <button 
              type="button"
              className="btn btn-primary"
              onClick={() => window.location.href = '/cart/'}
            >
              <i className="fas fa-shopping-cart me-2"></i>
              Go to Checkout
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Helper function to get field icon based on field type
  const getFieldIcon = (fieldType: string): string => {
    const iconMap: { [key: string]: string } = {
      'text': 'font',
      'email': 'envelope',
      'tel': 'phone',
      'number': 'hashtag',
      'date': 'calendar',
      'select': 'list',
      'textarea': 'align-left',
      'url': 'link',
      'password': 'lock'
    };
    return iconMap[fieldType] || 'edit';
  };

  // Helper function to render form fields
  const renderField = (field: EventField | CategoryField, value: any, onChange: (value: string) => void, namePrefix = '') => {
    const fieldName = namePrefix ? `${namePrefix}_${field.name}` : field.name;
    const fieldValue = value || '';

    if (field.field_type === 'select' && field.options) {
      return (
        <select
          className="form-select simple-input"
          name={fieldName}
          value={fieldValue}
          onChange={(e) => onChange(e.target.value)}
          required={field.required}
        >
          <option value="">Select {field.label}</option>
          {Object.entries(field.options).map(([key, val]) => (
            <option key={val} value={val}>{key}</option>
          ))}
        </select>
      );
    }

    if (field.field_type === 'textarea') {
      return (
        <textarea
          className="form-control simple-input"
          rows={3}
          name={fieldName}
          value={fieldValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Enter ${field.label.toLowerCase()}`}
          required={field.required}
        />
      );
    }

    return (
      <input
        type={field.field_type}
        className="form-control simple-input"
        name={fieldName}
        value={fieldValue}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`Enter ${field.label.toLowerCase()}`}
        required={field.required}
      />
    );
  };

  // Check if category is sold out
  const isSoldOut = category.sold_out || category.remaining <= 0;

  if (isSoldOut) {
    return (
      <div className="general-registration">
        <div className="glass p-4 rounded-3 q-shadow text-center">
          <div className="sold-out-icon mb-3">
            <i className="fas fa-times-circle text-danger" style={{ fontSize: '3rem' }}></i>
          </div>
          <h3 className="text-danger mb-3">Sold Out</h3>
          <p className="text-muted mb-4">
            Sorry, <strong>{category.name}</strong> is currently sold out.
          </p>
          <p className="text-muted mb-4">
            {event.content.sold_out_text || 'Please check back later or contact the organizer for more information.'}
          </p>
          <div className="d-flex gap-3 justify-content-center">
            <button 
              type="button"
              className="btn btn-outline-secondary"
              onClick={onCancel}
            >
              <i className="fas fa-arrow-left me-2"></i>
              Back to Categories
            </button>
            <button 
              type="button"
              className="btn btn-primary"
              onClick={() => window.location.href = '/events/'}
            >
              <i className="fas fa-calendar me-2"></i>
              View Other Events
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="general-registration">
      <div className="glass p-4 rounded-3 q-shadow">
        <div className="text-center mb-4">
          <h4 className="text-primary mb-2">
            <i className="fas fa-user-plus me-2"></i>
            General Registration
          </h4>
          <p className="text-muted mb-2">Register for {category.name}</p>
          {event.setting.show_remaining_passes && category.remaining && category.remaining > 0 && (
            <div className="d-flex justify-content-center align-items-center gap-3 mb-2">
              <div className="badge bg-primary-alpha text-primary">
                <i className="fas fa-ticket-alt me-1"></i>
                {category.remaining} Left
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="alert alert-danger mb-3" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        <Form onSubmit={handleSubmit}>
            {/* Personal Information */}
            <div className="form-group">
              <label htmlFor="name" className="text-left">
                Full Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control simple-input"
                name="name"
                id="name"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email" className="text-left">
                Email Address <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                className="form-control simple-input"
                name="email"
                id="email"
                placeholder="Enter your email address"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <InternationalPhoneInput
                value={formData.phone}
                onChange={(value) => {
                  // Ensure phone number is properly formatted (without + prefix)
                  const formattedPhone = value ? value.replace(/^\+/, '') : '';
                  setFormData(prev => ({ ...prev, phone: formattedPhone }));
                }}
                placeholder="Enter your phone number"
                id="phone"
                name="phone"
                label="Phone Number"
                showLabel={true}
                required={true}
              />
            </div>

            {/* Event Fields */}
            {eventFields.map((field) => (
              <div key={field.id} className="form-group">
                <label htmlFor={field.name} className="text-left">
                  {field.label} {field.required && <span className="text-danger">*</span>}
                </label>
                {renderField(field, formData[field.name] || '', (value) => 
                  setFormData(prev => ({ ...prev, [field.name]: value }))
                )}
              </div>
            ))}

            {/* Category Fields */}
            {categoryFields.map((field) => (
              <div key={field.id} className="form-group">
                <label htmlFor={field.name} className="text-left">
                  {field.label} {field.required && <span className="text-danger">*</span>}
                </label>
                {renderField(field, formData[field.name] || '', (value) => 
                  setFormData(prev => ({ ...prev, [field.name]: value }))
                )}
              </div>
            ))}

            {/* Quantity Selection */}
            <div className="form-group">
              <label htmlFor="quantity" className="text-left">
                Number of Passes <span className="text-danger">*</span>
              </label>
              <select
                className="form-select simple-input"
                name="quantity"
                id="quantity"
                value={formData.quantity}
                onChange={handleQuantityChange}
                required
              >
                <option value="">Select quantity</option>
                {Array.from({ 
                  length: Math.min(category.remaining || category.quota || 10, category.quota || 10) 
                }, (_, i) => i + 1).map(num => (
                  <option key={num} value={num}>
                    {num} {num === 1 ? 'pass' : 'passes'}
                  </option>
                ))}
              </select>
              {event.setting.show_remaining_passes && category.remaining && category.remaining < (category.quota || 10) && (
                <small className="text-muted mt-1 d-block">
                  <i className="fas fa-info-circle me-1"></i>
                  Only {category.remaining} passes remaining
                </small>
              )}
            </div>

            {/* Additional Attendees */}
            {event?.setting?.collect_individual_information && additionalAttendees.length > 0 && (
              <div className="mt-4">
                <h6 className="text-center text-primary mb-3">
                  <i className="fas fa-users me-2"></i>
                  Additional Attendee Information
                </h6>
                {additionalAttendees.map((attendee, index) => (
                  <div key={index} className="glass p-3 rounded-3 mb-3">
                    <h6 className="text-primary mb-3">
                      <i className="fas fa-user me-2"></i>
                      Additional Attendee {index + 1}
                    </h6>
                    
                    <div className="form-group">
                      <label htmlFor={`attendee_name_${index}`} className="text-left">
                        Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control simple-input"
                        id={`attendee_name_${index}`}
                        value={attendee.name}
                        onChange={(e) => handleAttendeeChange(index, 'name', e.target.value)}
                        required
                        placeholder="Enter attendee name"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor={`attendee_email_${index}`} className="text-left">
                        Email
                      </label>
                      <input
                        type="email"
                        className="form-control simple-input"
                        id={`attendee_email_${index}`}
                        value={attendee.email}
                        onChange={(e) => handleAttendeeChange(index, 'email', e.target.value)}
                        placeholder="Enter attendee email"
                      />
                    </div>

                    <div className="form-group">
                      <InternationalPhoneInput
                        value={attendee.phone}
                        onChange={(value) => handleAttendeeChange(index, 'phone', value || '')}
                        placeholder="Enter attendee phone"
                        id={`attendee_phone_${index}`}
                        name={`attendee_phone_${index}`}
                        label="Phone"
                        showLabel={true}
                        required={false}
                      />
                    </div>
                    
                    {/* Event fields for additional attendees */}
                    {eventFields.map((field) => (
                      <div key={field.id} className="form-group">
                        <label htmlFor={`attendee_${index}_${field.name}`} className="text-left">
                          {field.label} {field.required && <span className="text-danger">*</span>}
                        </label>
                        {renderField(field, attendee[field.name], (value) => 
                          handleAttendeeChange(index, field.name, value)
                        )}
                      </div>
                    ))}
                    
                    {/* Category fields for additional attendees */}
                    {categoryFields.map((field) => (
                      <div key={field.id} className="form-group">
                        <label htmlFor={`attendee_${index}_${field.name}`} className="text-left">
                          {field.label} {field.required && <span className="text-danger">*</span>}
                        </label>
                        {renderField(field, attendee[field.name], (value) => 
                          handleAttendeeChange(index, field.name, value)
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {/* Action Buttons */}
            <div className="registration-actions">
              <button
                type="button"
                className="btn btn-cancel"
                onClick={onCancel}
                disabled={loading}
              >
                <i className="fas fa-times"></i>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane me-2"></i>
                    {event?.content?.button_label || 'Register Now'}
                  </>
                )}
              </button>
            </div>
          </Form>
        </div>
    </div>
  );
};

export default GeneralRegistration;
